import tempMeeApi from '../apis/tempMee';

/**
 * Fetches GoTu feedback data for a specific user.
 *
 * @param {string} id - The unique identifier of the user.
 * @returns {Promise<Array<{
 *   createdAt: string,
 *   updatedAt: string,
 *   deviceType: string | null,
 *   feedbackNotes: string,
 *   rating: string | null,
 *   ratingDesc: string | null
 * }>>} A promise that resolves to an array of feedback objects containing:
 *   - `createdAt` (string): The date when the feedback was submitted.
 *   - `updatedAt` (string): The date when the feedback was last updated.
 *   - `deviceType` (string | null): The type of device used.
 *   - `feedbackNotes` (string): The actual feedback provided.
 *   - `rating` (string | null): The rating given.
 *   - `ratingDesc` (string | null): Description of the rating.
 *
 * @throws {Error} If the request fails.
 */
export const getGoTuFeedback = async (id) => {
  const response = await tempMeeApi.get(`admin/users/${id}/gotu-feedback`);

  return response.data.data;
};

export const getUser = async (id) => {
  const response = await tempMeeApi.get(`admin/users/${id}`);

  return response.data;
};

export const patchUser = async (userId, data = {}) => {
  return tempMeeApi.patch(`admin/users/${userId}`, data);
};

/**
 *
 * @param {string} id - The unique identifier of the user.
 * @param {Object} payload - The parameters object.
 * @param {string} payload.firstName - The first name of the user.
 * @param {string} payload.lastName - The last name of the user.
 * @param {string} payload.email - The email address of the user.
 * @param {string} payload.phone - The phone number of the user.
 *
 * @details https://bump.sh/gotu/hub/gotu-all-services/doc/auth-service/operation/operation-patch-admin-users-id
 *
 * @returns {Promise<void>} A promise that resolves to the updated user object.
 * @throws {Error} If the request fails.
 */
export const updateUser = async (id, payload = {}) => {
  const response = await tempMeeApi.patch(`/auth-service/admin/users/${id}`, payload);

  return response.data;
};

/**
 * Updates the "Best in Dental" status for a user.
 *
 * @param {string} id - The unique identifier of the user.
 * @param {Object} payload - The parameters object.
 * @param {string} payload.status - The status of Best in Dental.
 *   Must be one of: "not-entered", "nominee", "applicant", "winner", or "rejected".
 *
 * @returns {Promise<void>} A promise that resolves when the update is successful.
 * @throws {Error} If the request fails.
 */
export const updateUserBestInDental = async (id, payload = {}) => {
  return tempMeeApi.patch(`admin/users/${id}/best-in-dental`, payload);
};
